import { Component } from 'react';
import { debounce } from 'lodash';
import TextInput from 'src/components/inputs/textInput';
import { isUsernameAvailable } from 'src/modules/shared/auth/actions';
import i18n from 'src/utils/translate';
import { required, minLength, maxLength, username } from 'src/utils/validators';

const validators = [
  required('Username is required'),
  minLength(4, 'Usernames must be at least 4 characters long'),
  maxLength(20, 'Usernames must not be longer than 20 characters'),
  username(),
];

type Props = {
  setUsername: (username: string) => void;
}
type State = {
  error?: string;
  value: string;
  hasChanged: boolean;
}

export default class ChooseUsernameForm extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      value: '',
      hasChanged: false,
    };
  }

  asyncValidate = async (value) => {
    const isAvailable = await isUsernameAvailable(value);
    if (!isAvailable) {
      return i18n.t('Username is not available.');
    }
    return undefined;
  }

  validate = debounce(async (username) => {
    let hasError = false;
    validators.forEach((validateField) => {
      if (!hasError) {
        const error = validateField(username);
        if (error) {
          hasError = true;
          this.setState({ error });
        }
      }
    });
    if (!hasError) {
      const error = await this.asyncValidate(username);
      if (error) {
        this.setState({ error });
        hasError = true;
      }
    }
    this.props.setUsername(!hasError ? username : null);
  }, 300);

  onChange = (e) => {
    const username = e.target.value;
    this.setState({ error: null, value: username }, () => {
      this.validate(username);
      // we only want to display the validation error after a 2sec delay
      if (!this.state.hasChanged) {
        setTimeout(() => {
          this.setState({ hasChanged: true });
        }, 2000);
      }
    });
  }

  onKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }

  render() {
    return (
      <TextInput
        dataTestId="signup-survey-username"
        id="username"
        placeholder={i18n.t('Choose your username')}
        type="text"
        validationErrors={this.state.hasChanged && !!this.state.error ? [this.state.error] : []}
        value={this.state.value}
        onChange={this.onChange}
        onKeyPress={this.onKeyPress}
      />
    );
  }
}
